import { Clapperboard, Cpu, Image, KeyRound, LogOut, Menu, Package, Palette, Users, UsersRound, X } from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { GpuCard } from './GpuCard';
import { PasswordDialog } from './PasswordDialog';

const NAV = [
  { to: '/', label: 'Projects', icon: Clapperboard },
  { to: '/characters', label: 'Characters', icon: UsersRound },
  { to: '/backgrounds', label: 'Backgrounds', icon: Image },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/brand', label: 'Brand & knowledge', icon: Palette },
  { to: '/gpu', label: 'GPU & costs', icon: Cpu },
  { to: '/users', label: 'Users', icon: Users, admin: true },
];

function Nav({ onNavigate }: { onNavigate?: () => void }) {
  const { isAdmin } = useAuth();
  return (
    <nav className="flex flex-col gap-0.5" aria-label="Main">
      {NAV.filter((n) => !n.admin || isAdmin).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-sidebar-active text-sidebar-active-foreground' : 'text-sidebar-foreground hover:bg-accent',
            )
          }
        >
          <item.icon className="size-4 shrink-0" aria-hidden />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2 px-2">
      <img src="/logo-mark.png" alt="Herbal Cure" className="size-10" />
      <div className="leading-tight">
        <div className="font-semibold tracking-tight">Herbal Cure</div>
        <div className="text-xs text-muted-foreground">Video Studio</div>
      </div>
    </div>
  );
}

export function Layout() {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState(false);
  const sidebar = (
    <div className="flex h-full flex-col gap-5 p-3">
      <Brand />
      <Nav onNavigate={() => setOpen(false)} />
      <div className="mt-auto space-y-3">
        <GpuCard compact />
        <div className="rounded-lg border p-2.5 text-sm">
          <div className="truncate font-medium">{me?.user.name}</div>
          <div className="truncate text-xs text-muted-foreground">{me?.user.email}</div>
          <div className="mt-2 flex items-center gap-1">
            <Badge variant="secondary">{me?.user.role}</Badge>
            {me?.gpuMode === 'fake' && <Badge variant="warning">fake GPU</Badge>}
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto size-7"
              onClick={() => setPw(true)}
              aria-label="Change password"
              title="Change password"
            >
              <KeyRound />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => void logout()}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[250px_1fr]">
      <aside className="sticky top-0 hidden h-screen border-r bg-sidebar lg:block">{sidebar}</aside>
      <div className="flex items-center gap-2 border-b bg-sidebar px-3 py-2 lg:hidden">
        <Button variant="ghost" size="icon" onClick={() => setOpen(true)} aria-label="Menu">
          <Menu />
        </Button>
        <Brand />
      </div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-sidebar shadow-lg">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
            >
              <X />
            </Button>
            {sidebar}
          </aside>
        </div>
      )}
      <main className="min-w-0 p-4 sm:p-6">
        <Outlet />
      </main>
      <PasswordDialog open={pw} onOpenChange={setPw} />
    </div>
  );
}
